import React, { useEffect, useState, useMemo } from 'react';
import { Card, Tag, Tooltip, Flex, theme, message as antdMessage } from 'antd';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../../../../Shared/GlobalContext/ThemeContext';
import { sendMessage } from '../../../../MessagesServices';
import { downloadFile } from '../../../../../../Utils/capacitorDownload';
import { RTL_LANGUAGES } from '../../../../../../Constants/language';
import MessageMediaGallery from './MessageMediaGallery/MessageMediaGallery';
import MessageBubbleActions from './MessageBubbleActions/MessageBubbleActions';
import MessageMetadata from './MessageMetadata/MessageMetadata';
import MessageSpecialStates from './MessageSpecialStates/MessageSpecialStates';
import QuotedMessagePreview from './QuotedMessagePreview/QuotedMessagePreview';
import useMessageGestures from './hooks/useMessageGestures';
import { renderTextWithLinks } from '../../../../utils/messageRenderUtils';
import { Typography } from 'antd';

const { Text } = Typography;

const MessageBubble = ({ message, onReplyClick, onReplyAction, messageRefs, onMessageSelect, invertMessageSides = false, isViewOnly = false, screens }) => {
    const isAgentOrSupport = message.sender_type === 'agent' || message.sender_type === 'support';
    // When invertMessageSides is true, we flip the logic: agent messages go left, customer messages go right
    const shouldShowOnRight = invertMessageSides ? !isAgentOrSupport : isAgentOrSupport;
    const { token } = theme.useToken();
    const { isDarkMode } = useTheme();
    const { t, i18n } = useTranslation();
    const isRtl = RTL_LANGUAGES.includes(i18n.language.split('-')[0]);

    const isFacebookPolicyError = message.error_type === 'sending_error' &&
        message.error_details && message.error_details.includes('Facebook business policy');

    const [resolving, setResolving] = useState(false);
    const [retrying, setRetrying] = useState(false);
    const [contextMenuOpen, setContextMenuOpen] = useState(false);
    const [isHovered, setIsHovered] = useState(false);

    const isFailed = Boolean(message?.status?.failed) || message?.failed_at || message?.error_type === 'sending_error';
    const isEscalation = Boolean(message?.has_ticket) && message?.ticket?.status !== 'resolved' && message?.ticket?.status !== 'closed';
    const isAbuse = Boolean(message?.is_abuse);
    const canReplyToThis = !isViewOnly && true;
    const hasMedia = Array.isArray(message.medias) && message.medias.length > 0;

    const messageBubbleBackground = isDarkMode
        ? shouldShowOnRight
            ? token.colorPrimary
            : '#2a2a2a'
        : shouldShowOnRight
            ? token.colorPrimary
            : '#fff';

    const messageBubbleTextColor = isDarkMode
        ? shouldShowOnRight
            ? '#fff'
            : '#fff'
        : shouldShowOnRight
            ? '#fff'
            : 'inherit';

    const messageAbuseTagColor = isDarkMode ? 'rgba(255, 255, 255, 0.8)' : 'rgba(0, 0, 0, 0.45)';

    const {
        translateX,
        handleTouchStart,
        handleTouchMove,
        handleTouchEnd,
        handleTouchCancel
    } = useMessageGestures({
        screens,
        isRtl,
        shouldShowOnRight,
        canReplyToThis,
        onReplyAction,
        message
    });

    const handleResolve = async (resolveText) => {
        setResolving(true);
        try {
            // Here you would normally call the API to resolve
            // For now simulating success as in original code
            // Replace with actual API call if available
            antdMessage.success(t('escalation_resolved'));
            return Promise.resolve();
        } catch (e) {
            antdMessage.error(t('something_went_wrong'));
            return Promise.reject();
        } finally {
            setResolving(false);
        }
    };

    const handleRetry = async () => {
        if (!message?.conversation_id) {
            antdMessage.error(t('retry_failed'));
            return;
        }

        setRetrying(true);
        try {
            const attachmentIds = Array.isArray(message.medias)
                ? message.medias
                    .map(m => m?.id)
                    .filter(Boolean)
                : [];

            const payload = {
                content: message?.content || '',
                attachment_ids: attachmentIds,
            };

            if (message?.reply_to_message?.id) {
                payload.reply_to_message_id = message.reply_to_message.id;
            }

            await sendMessage(message.conversation_id, payload);
            antdMessage.success(t('retry_sent'));
        } catch (e) {
            antdMessage.error(t('retry_failed'));
        } finally {
            setRetrying(false);
        }
    };

    const handleAction = (actionKey) => {
        switch (actionKey) {
            case 'reply':
                if (canReplyToThis && typeof onReplyAction === 'function') {
                    onReplyAction(message);
                }
                break;
            case 'copy':
                try {
                    if (message?.content) {
                        navigator.clipboard?.writeText?.(message.content);
                        antdMessage.success(t('copied'));
                    }
                } catch (e) {
                    antdMessage.error(t('copy_failed'));
                }
                break;
            case 'details':
                if (typeof onMessageSelect === 'function') {
                    onMessageSelect(message);
                }
                break;
            case 'download':
                if (!message?.medias || message.medias.length === 0) return;
                message.medias.forEach(media => {
                    const filename = media.filename || media.url.substring(media.url.lastIndexOf('/') + 1);
                    downloadFile(media.url, filename);
                });
                break;
            default:
                break;
        }
    };

    useEffect(() => {
        if (messageRefs && message.id) {
            messageRefs.current[message.id] = React.createRef();
        }
    }, [message.id, messageRefs]);

    const containerStyle = {
        display: 'flex',
        justifyContent: 'flex-start',
        width: '100%',
    };

    const bubbleStyle = {
        maxWidth: screens.xs ? '90%' : '75%',
        width: hasMedia && !message.content ? 'auto' : (hasMedia ? '100%' : 'auto'),
        marginInlineStart: shouldShowOnRight ? 'auto' : '0',
        marginInlineEnd: shouldShowOnRight ? '0' : 'auto',
    };

    return (
        <div style={containerStyle}>
            <div
                ref={messageRefs?.current[message.id]}
                style={{
                    ...bubbleStyle,
                    position: 'relative',
                    transform: `translateX(${translateX}px)`,
                    transition: translateX !== 0 ? 'none' : 'transform 0.2s ease-out'
                }}
                onMouseEnter={() => !screens.xs && setIsHovered(true)}
                onMouseLeave={() => !screens.xs && setIsHovered(false)}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                onTouchCancel={handleTouchCancel}
                onKeyDown={(e) => {
                    if ((e.key === 'r' || e.key === 'R') && canReplyToThis && typeof onReplyAction === 'function') {
                        e.preventDefault();
                        onReplyAction(message);
                    }
                }}
                tabIndex={0}
                role="group"
                aria-label={t('message')}
            >
                <div style={{ position: 'relative', paddingBottom: message.reactions?.length > 0 ? 12 : 0 }}>
                    <MessageBubbleActions
                        message={message}
                        hasMedia={hasMedia}
                        canReplyToThis={canReplyToThis}
                        onAction={handleAction}
                        isHovered={isHovered}
                        contextMenuOpen={contextMenuOpen}
                        setContextMenuOpen={setContextMenuOpen}
                        isMobile={screens.xs}
                        isViewOnly={isViewOnly}
                    >
                        <Card
                            size="small"
                            style={{
                                background: messageBubbleBackground,
                                color: messageBubbleTextColor,
                                width: hasMedia && !message.content ? 'auto' : '100%',
                                cursor: 'default',
                                opacity: isFacebookPolicyError ? 0.7 : 1,
                            }}
                            styles={{ body: { padding: hasMedia && !message.content ? 6 : '5px 12px' } }}
                        >
                            {isFacebookPolicyError && (
                                <Tag
                                    color="orange"
                                    style={{
                                        position: 'absolute',
                                        bottom: -15,
                                        insetInlineStart: 8,
                                        fontWeight: 'bold',
                                        borderRadius: 4,
                                        border: 'none',
                                        fontSize: '10px'
                                    }}
                                >
                                    ⚠️ {t('facebook_policy_violation')}
                                </Tag>
                            )}

                            {message.reply_to_message && (
                                <QuotedMessagePreview
                                    reply_to_message={message.reply_to_message}
                                    isCustomerMessage={shouldShowOnRight}
                                    onClick={() => onReplyClick && onReplyClick(message.reply_to_message)}
                                    isDarkMode={isDarkMode}
                                />
                            )}

                            <Flex vertical size={4} style={{ width: '100%' }}>
                                {message.content && (
                                    <div>
                                        {renderTextWithLinks(message.content, shouldShowOnRight, isDarkMode)}
                                    </div>
                                )}

                                {isFacebookPolicyError && (
                                    <div style={{
                                        marginTop: 4,
                                        padding: 6,
                                        background: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 152, 0, 0.1)',
                                        borderRadius: 4,
                                        borderLeft: '3px solid orange'
                                    }}>
                                        <Text
                                            style={{
                                                display: 'block',
                                                fontSize: '11px',
                                                color: isDarkMode ? 'rgba(255, 255, 255, 0.8)' : 'rgba(0, 0, 0, 0.65)',
                                                lineHeight: 1.4,
                                            }}
                                        >
                                            {message.error_details}
                                        </Text>
                                        <Tooltip title={message.error_details}>
                                            <Text
                                                underline
                                                style={{
                                                    fontSize: '10px',
                                                    color: isDarkMode ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.45)',
                                                    cursor: 'help'
                                                }}
                                            >
                                                {t('learn_more')}
                                            </Text>
                                        </Tooltip>
                                    </div>
                                )}

                                <MessageMediaGallery
                                    mediaList={message.medias}
                                    isCustomerMessage={shouldShowOnRight}
                                    isDarkMode={isDarkMode}
                                    handleTouchStart={handleTouchStart}
                                    handleTouchMove={handleTouchMove}
                                    handleTouchEnd={handleTouchEnd}
                                    handleTouchCancel={handleTouchCancel}
                                />

                                {!message.content && (!message.medias || message.medias?.length === 0) && isAgentOrSupport && (
                                    <div>
                                        <Text style={{
                                            color: messageAbuseTagColor,
                                            fontSize: '10px'
                                        }}>
                                            {t('agent_declined_to_respond')}
                                        </Text>
                                    </div>
                                )}

                                <MessageMetadata
                                    message={message}
                                    isCustomerMessage={shouldShowOnRight}
                                    isDarkMode={isDarkMode}
                                    isFacebookPolicyError={isFacebookPolicyError}
                                    messageAbuseTagColor={messageAbuseTagColor}
                                />
                            </Flex>
                        </Card>
                    </MessageBubbleActions>

                    {/* Reactions badge - positioned at the bottom edge of the bubble */}
                    {message.reactions?.length > 0 && (
                        <div style={{
                            position: 'absolute',
                            bottom: -10,
                            insetInlineEnd: 8,
                            display: 'flex',
                            gap: 2,
                            zIndex: 1,
                        }}>
                            {message.reactions.map((reaction, index) => (
                                <Tag key={index} style={{
                                    padding: '0 4px',
                                    fontSize: '13px',
                                    margin: 0,
                                    borderRadius: 10,
                                    background: isDarkMode ? '#2a2a2a' : '#fff',
                                    border: `1px solid ${isDarkMode ? '#434343' : '#e8e8e8'}`,
                                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                                    lineHeight: '20px',
                                    cursor: 'default',
                                }}>
                                    {reaction.emoji}
                                </Tag>
                            ))}
                        </div>
                    )}
                </div>

                <MessageSpecialStates
                    message={message}
                    isAbuse={isAbuse}
                    isFailed={isFailed}
                    isEscalation={isEscalation}
                    isDarkMode={isDarkMode}
                    onResolve={handleResolve}
                    onRetry={handleRetry}
                    resolving={resolving}
                    retrying={retrying}
                />
            </div>
        </div>
    );
};

export default MessageBubble;
